import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect, ErpTable, Modal, toOptions } from "../../components/erp";
import { errorText } from "../../api/client";
import { csDropdownsApi, stockOutApi, StockOutRow } from "./api";

const today = () => new Date().toISOString().slice(0, 10);

const OutModal: React.FC<{ row: StockOutRow; onClose: () => void; onSaved: () => void }> = ({ row, onClose, onSaved }) => {
  const dd = useQuery({ queryKey: ["coldstore", "dropdowns"], queryFn: csDropdownsApi.get, staleTime: 5 * 60_000 });
  const outTypes = dd.data?.Data?.OutType || [];
  const [v, setV] = useState({ OutMcs: String(row.Mcs), OutLooseBags: String(row.Bags), StockOutReason: "", Remarks: "" });
  const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      const r = await stockOutApi.post({
        ProductionID: row.ProductionID, StockOutDate: today(), StockOutReason: v.StockOutReason, TotalMsc: v.OutMcs,
        TotalLooseBags: v.OutLooseBags, Remarks: v.Remarks,
        StockItems: [{ StockID: row.StockID, RackID: row.RackID, OutMcs: Number(v.OutMcs), OutLooseBags: Number(v.OutLooseBags), ProductionID: row.ProductionID }],
      });
      if (r.Error) setErr(r.Error); else onSaved();
    } catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={`Stock Out - #${row.StockID}`} onClose={onClose} testId="stockout-modal">
      <form onSubmit={submit} data-testid="stockout-form">
        <div className="row">
          <div className="col-md-4 mb-3"><label className="form-label">Out Mcs (avail {row.Mcs})</label>
            <input className="form-control" value={v.OutMcs} onChange={(e) => setV((s) => ({ ...s, OutMcs: e.target.value }))} data-testid="stockout-mcs" /></div>
          <div className="col-md-4 mb-3"><label className="form-label">Out Loose Bags (avail {row.Bags})</label>
            <input className="form-control" value={v.OutLooseBags} onChange={(e) => setV((s) => ({ ...s, OutLooseBags: e.target.value }))} data-testid="stockout-loose-bags" /></div>
          <div className="col-md-4 mb-3"><label className="form-label">Reason</label>
            <select className="form-select" value={v.StockOutReason} onChange={(e) => setV((s) => ({ ...s, StockOutReason: e.target.value }))} data-testid="stockout-reason">
              <option value="">Select</option>{outTypes.map((o) => <option key={o.OptID} value={o.Value}>{o.Name}</option>)}
            </select></div>
        </div>
        <div className="mb-3"><label className="form-label">Remarks</label>
          <input className="form-control" value={v.Remarks} onChange={(e) => setV((s) => ({ ...s, Remarks: e.target.value }))} data-testid="stockout-remarks" /></div>
        {err && <Alert kind="danger" testId="stockout-error">{err}</Alert>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid="stockout-submit">{saving ? "Saving..." : "Confirm Stock Out"}</button>
      </form>
    </Modal>
  );
};

/** Port of v3/ColdStore/stock-out.php - outward/issue from stock. Each item decrements the source
 * `CsStockData` row (supports genuine partial issue) and unconditionally sets StockStatus=2 - a
 * real source quirk preserved as-is (see MIGRATION.md). When a barcode's remaining balance across
 * all its stock rows reaches zero, the rack slot is freed automatically. */
export default function StockOutPage() {
  const [coldStoreId, setColdStoreId] = useState("");
  const [selectedRow, setSelectedRow] = useState<StockOutRow | null>(null);
  const qc = useQueryClient();
  const dd = useQuery({ queryKey: ["coldstore", "dropdowns"], queryFn: csDropdownsApi.get, staleTime: 5 * 60_000 });
  const coldstores = useMemo(() => toOptions(dd.data?.Data?.ColdStores, (c) => c.StoreID, (c) => c.StoreName), [dd.data]);
  const list = useQuery({ queryKey: ["coldstore", "stock-out", coldStoreId], enabled: !!coldStoreId, queryFn: () => stockOutApi.get({ ColdStore: coldStoreId }) });

  return (
    <>
      <PageHeader title="Stock Out" location="ColdStore" />
      <div className="card mb-3"><div className="card-body">
        <div className="row"><div className="col-md-3 mb-3"><label className="form-label">Coldstore</label>
          <ErpSelect id="stockout-coldstore" value={coldStoreId} onChange={setColdStoreId} options={coldstores} testId="stockout-coldstore-filter" /></div></div>
      </div></div>
      <div className="card"><div className="card-body">
        <ErpTable<StockOutRow> testId="stockout-table" loading={list.isLoading} error={list.isError ? errorText(list.error) : null}
          rows={list.data?.DailyStockOutData || []} columns={[
            { key: "StockID", title: "Stock ID" }, { key: "Variety", title: "Variety" }, { key: "Grade", title: "Grade" },
            { key: "Mcs", title: "Mcs" }, { key: "Bags", title: "Bags" }, { key: "Kgs", title: "Kgs" }, { key: "Location", title: "Location" },
            { key: "Action", title: "Action", render: (r) => (
              <button className="btn btn-sm btn-warning" data-testid={`stockout-issue-${r.StockID}`} onClick={() => setSelectedRow(r)}>Issue Out</button>
            ) }]} />
      </div></div>
      {selectedRow && <OutModal row={selectedRow} onClose={() => setSelectedRow(null)}
        onSaved={() => { setSelectedRow(null); qc.invalidateQueries({ queryKey: ["coldstore", "stock-out", coldStoreId] }); }} />}
    </>
  );
}
